import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const LOGO_TRANSPARENT = 'https://storage.readdy-site.link/project_files/9caf5728-6755-4c3f-97ef-9162838cb2ba/d6b8e6ce-2e67-441e-8e15-51ab25492342_movewith-black-horizontal.pdf.png?v=661240ab2fb876e643c2480c18fd5497';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setIsMobileMenuOpen(false); }, [location.pathname]);

  // On homepage: transparent until scroll. On other pages: transparent until scroll too,
  // but text stays dark since page content is light-colored behind.
  const isTransparent = !isScrolled;
  const isWhiteMode = isHome && isTransparent; // hero overlay = white logo & text needed

  const navBg = isScrolled
    ? 'bg-white shadow-sm'
    : 'bg-transparent';

  const logoSrc = LOGO_TRANSPARENT;

  const linkBase = 'text-sm font-medium tracking-wide transition-all duration-300 whitespace-nowrap';
  const linkIdle = isWhiteMode
    ? 'text-white/90 hover:text-white'
    : 'text-[#1A1A1A] hover:text-[#C8923A]';
  const linkActive = isWhiteMode ? 'text-white' : 'text-[#C8923A]';

  const iconColor = isWhiteMode
    ? 'text-white/80 hover:text-white'
    : 'text-[#1A1A1A] hover:text-[#C8923A]';

  const mobileButtonColor = isWhiteMode ? 'text-white' : 'text-[#1A1A1A]';

  const navLinks = [
    { name: 'About', path: '/about' },
    { name: 'Buying', path: '/buying' },
    { name: 'Selling', path: '/selling' },
    { name: 'Areas', path: '/areas' },
    { name: 'Resources', path: '/resources' },
    { name: 'Testimonials', path: '/testimonials' },
    { name: 'Giveaway', path: '/giveaway' },
    { name: 'Get In Touch', path: '/contact' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBg}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-24">

          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src={logoSrc}
              alt="Move With Kendrick"
              className="h-12 sm:h-16 lg:h-20 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`${linkBase} ${
                  location.pathname === link.path ? linkActive : linkIdle
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Right: Social + CTA */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Social Icons */}
            <div className="flex items-center gap-2 mr-2">
              <a
                href="https://www.instagram.com/movewithkendrick/"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 ${iconColor}`}
                aria-label="Instagram"
              >
                <i className="ri-instagram-line text-lg"></i>
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61561482914556"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 ${iconColor}`}
                aria-label="Facebook"
              >
                <i className="ri-facebook-fill text-lg"></i>
              </a>
            </div>

            {/* CTA — white-outlined on transparent hero, solid blue once scrolled */}
            <a
              href="tel:+15176629155"
              className={`px-5 py-2.5 text-sm font-semibold rounded-md transition-all duration-300 whitespace-nowrap inline-flex items-center gap-2 ${
                isWhiteMode
                  ? 'border border-white/70 text-white hover:bg-white/15'
                  : 'bg-[#1C3A6B] text-white hover:bg-[#152E55]'
              }`}
            >
              <i className="ri-phone-line"></i>
              Call Kendrick
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`xl:hidden w-10 h-10 flex items-center justify-center transition-colors duration-300 ${mobileButtonColor}`}
            aria-label="Toggle menu"
          >
            <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu — always solid white */}
      {isMobileMenuOpen && (
        <div className="xl:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-6 py-5 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block py-3 text-sm font-medium border-b border-gray-100 transition-colors ${
                  location.pathname === link.path
                    ? 'text-[#C8923A]'
                    : 'text-[#1A1A1A] hover:text-[#C8923A]'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <a href="https://www.instagram.com/movewithkendrick/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center text-[#1A1A1A] hover:text-[#C8923A]" aria-label="Instagram">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="https://www.facebook.com/profile.php?id=61561482914556" target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center text-[#1A1A1A] hover:text-[#C8923A]" aria-label="Facebook">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
              </div>
              <a
                href="tel:+15176629155"
                className="px-5 py-2.5 bg-[#1C3A6B] text-white text-sm font-semibold rounded-md hover:bg-[#152E55] transition-all whitespace-nowrap"
              >
                Call Kendrick
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
